import { Fragment, useCallback, useEffect, useRef, useState, type ReactNode } from 'react'
import apiClient from '../../api/client'
import { errorMessage } from '../../api/ApiError'
import StatusPill from '../StatusPill'
import { VisorDeDocumento } from '../VisorDocumento'
import {
  agruparPorDia, etiquetaDeVersion, etiquetaDelDia, fechaHoraEnLima, intentosAnteriores,
  separarRechazados,
} from '../../documentos'
import { filasDeLaFicha } from '../../ficha'
import { alertasParaRevisar, cuantasCriticas } from '../../alertas'
import {
  marcaDelDocumento, ofrecerPedirDeNuevo, type MarcaDeRevision,
} from '../../revision'
import { cruceDelRecibo, filasDelRecibo } from '../../recibo'
import {
  SIN_VEREDICTO, lecturaDelVeredicto, type LecturaDelVeredicto, type Tono,
} from '../../veredicto'
import {
  MOTIVOS_DE_RECHAZO, agruparPorRequisito, cuantosPorDecidir, gruposDelFiltro, motivoSugerido,
  plegadoDeLaFila, seccionesDeDocumentos, type ArchivoDelRequisito, type EstadoDelRequisito, type FiltroDeRequisitos,
  type GrupoDelRequisito,
} from '../../documentosDelCaso'
import { descargarDocumento, descargarVarios } from '../../descargas'
import type {
  AlertaParaRevisar, Copilot, ConversationDetail, ConversationDocument, DocumentosPorAlcance,
  ProgresoDocumental, ResumenDerivacion,
} from '../../types/portal'

/**
 * Columna 3 de la bandeja — panel de contexto, 372 px. Por debajo de `xl` va
 * en un cajón, con el mismo ancho (`PanelDelCaso`, DEF-031).
 *
 * Tres pestañas: Copiloto IA · Documentos · Ficha. Es lo que evita que el
 * asesor navegue a otro módulo para revisar un DNI o mirar los datos del
 * cliente en medio de una conversación.
 */

type Tab = 'copiloto' | 'docs' | 'ficha'

const TABS: Array<{ key: Tab; label: string }> = [
  { key: 'copiloto', label: 'Copiloto IA' },
  { key: 'docs', label: 'Documentos' },
  { key: 'ficha', label: 'Ficha' },
]

interface ContextPanelProps {
  detail: ConversationDetail
  copilot: Copilot | null
  copilotLoading: boolean
  /**
   * El Copiloto que se muestra ya no corresponde al caso —llegaron mensajes
   * después— o se está pidiendo de nuevo (DEF-029, `useCopiloto`).
   */
  copilotUpdating?: boolean
  /** Reintenta el copiloto — Bedrock puede fallar por throttling. */
  onRetryCopilot: () => void
  /** Precarga el borrador del compositor con la acción recomendada. */
  onUseAction: (texto: string) => void
  /** `motivo`: el que eligió el ejecutivo al rechazar; viaja como `notes`. */
  onReviewDoc: AlRevisar
  reviewingDoc: string | null
  /** Refresca el detalle tras subir un documento desde el panel. */
  onDocumentoSubido: () => void
  /** Pestaña activa. La controla el padre para poder resetearla al cambiar
   *  de conversación. */
  tab: Tab
  onTabChange: (tab: Tab) => void
  /**
   * Solo lectura (En vivo): se ocultan las acciones que escriben. El resumen,
   * la intención, la objeción, los documentos y la ficha SÍ se muestran — son
   * justamente el contexto que el supervisor viene a leer.
   */
  readOnly?: boolean
  /**
   * En solo lectura, validar o rechazar archivos igual (En vivo, Supervisor y
   * Admin; Ruben, 20-oct-2026). Es una decisión sobre el documento, no una
   * atención: no toma la conversación ni le habla al cliente, así que «Pedir
   * de nuevo», el aviso del rechazo y «Subir» siguen fuera de En vivo. En Mi
   * bandeja no hace falta: ahí el ejecutivo decide siempre.
   */
  puedeDecidir?: boolean
  /**
   * En el cajón (DEF-031): cómo cerrarlo. Con esto el panel lleva su botón de
   * cerrar y llena el alto del cajón; sin esto es la columna de siempre.
   */
  onCerrar?: () => void
}

export type { Tab as ContextTab }

/** Validar (`approve`), rechazar (`reject`, con su motivo) o pedir de nuevo. */
export type AlRevisar = (docId: string, accion: 'approve' | 'reject' | 'request',
                         motivo?: string) => void

export default function ContextPanel({
  detail, copilot, copilotLoading, copilotUpdating = false, onRetryCopilot, onUseAction,
  onReviewDoc, reviewingDoc, tab, onTabChange, onDocumentoSubido,
  readOnly = false, puedeDecidir = false, onCerrar,
}: ContextPanelProps) {
  // Quién valida o rechaza: el ejecutivo en su bandeja, y supervisión desde En
  // vivo sin tomar la conversación.
  const decide = !readOnly || puedeDecidir
  // El badge cuenta los requisitos del expediente VIGENTE que la IA observó:
  // lo que espera la decisión del ejecutivo —validarlo igual o rechazarlo—
  // (20-oct-2026). Lo que la IA validó no es pendiente. Un documento del
  // expediente que el cliente descartó no cuenta.
  const porDecidir = cuantosPorDecidir(detail.documents, detail.progreso?.requeridos)
  // El histórico se pide cuando el asesor abre «Documentos», una vez por
  // conversación: nunca en el sondeo de 2,5 s del detalle.
  const anteriores = useDocumentosAnteriores(detail.conversation_id, tab === 'docs')

  return (
    <div className={`flex min-h-0 shrink-0 flex-col border-l border-surface-border bg-surface ${
      onCerrar ? 'h-full w-[372px]' : 'w-[312px] xl:w-[372px]'}`}>
      <div className="flex shrink-0 border-b border-surface-border bg-white">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => onTabChange(t.key)}
            className={`flex flex-1 items-center justify-center gap-1.5 border-b-2 px-1 py-2.5 text-body font-bold transition-colors ${
              tab === t.key
                ? 'border-brand text-ink'
                : 'border-transparent text-ink-faint hover:text-ink-muted'
            }`}
          >
            {t.label}
            {t.key === 'docs' && porDecidir > 0 && (
              <span className="rounded-chip bg-warn-light px-1.5 py-px text-[10px] font-bold text-warn"
                    title="Requisitos observados por la IA: por decidir">
                {porDecidir}
              </span>
            )}
          </button>
        ))}
        {onCerrar && (
          <button
            type="button"
            onClick={onCerrar}
            aria-label="Cerrar el panel del caso"
            title="Cerrar (Esc)"
            className="shrink-0 px-3 text-body font-bold text-ink-faint transition-colors hover:text-ink focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-brand"
          >
            ✕
          </button>
        )}
      </div>

      <div className="col-scroll scrollbar-thin flex-1 p-3.5">
        {tab === 'copiloto' && (
          <TabCopiloto
            copilot={copilot}
            loading={copilotLoading}
            updating={copilotUpdating}
            onRetry={onRetryCopilot}
            onUseAction={onUseAction}
            readOnly={readOnly}
            alertas={alertasParaRevisar(detail.alertas_revisar, detail.alertas_internas)}
            resumen={detail.resumen_derivacion}
            casoId={detail.conversation_id}
          />
        )}
        {tab === 'docs' && (
          <TabDocumentos
            documentos={detail.documents}
            requeridos={detail.progreso?.requeridos ?? []}
            anteriores={anteriores}
            hayAnteriores={(detail.documentos_anteriores ?? 0) > 0}
            onReview={onReviewDoc}
            reviewing={reviewingDoc}
            readOnly={readOnly}
            decide={decide}
            conversationId={detail.conversation_id}
            onDocumentoSubido={onDocumentoSubido}
            pedidoDesde={detail.checklist_generated_at ?? ''}
          />
        )}
        {tab === 'ficha' && (
          <TabFicha detail={detail} onReview={onReviewDoc} reviewing={reviewingDoc} readOnly={readOnly}
                    decide={decide} />
        )}
      </div>
    </div>
  )
}

// ── Copiloto IA ──────────────────────────────────────────────────────────────

/**
 * Lo que hay que REVISAR, no lo que pasó.
 *
 * El copiloto se arma con Bedrock sobre los mensajes, así que cuenta la
 * conversación: no ve los documentos, ni la partida, ni el cruce del titular.
 * Esto son los hallazgos del pipeline documental, tal como los escribió — sin
 * pasar por el modelo, porque son afirmaciones sobre documentos y un resumen
 * generado puede matizarlas o perderlas.
 *
 * Va ARRIBA del resumen y FUERA de los estados del copiloto: cuando Bedrock
 * falla o está cargando, lo que el asesor tiene que revisar sigue ahí. Que la
 * ayuda se caiga no puede llevarse puesto el hecho.
 *
 * Las CRÍTICAS van primero y en rojo (DEF-039): un embargo no puede pesar lo
 * mismo que una hipoteca. Lo ordena `alertas.ts`.
 */
function QueRevisar({ alertas }: { alertas: AlertaParaRevisar[] }) {
  if (alertas.length === 0) return null
  const criticas = cuantasCriticas(alertas)
  return (
    <div className="rounded-panel border border-warn-border bg-warn-light p-4">
      <p className="text-meta font-bold uppercase tracking-[0.05em] text-warn">
        Qué revisar ({alertas.length})
        {criticas > 0 && (
          <span className="text-danger"> · {criticas} crítica{criticas === 1 ? '' : 's'}</span>
        )}
      </p>
      <ul className="mt-2 flex flex-col gap-2">
        {alertas.map((a, i) => a.critica ? (
          <li key={i}
              className="flex gap-2 rounded-field border border-danger-border bg-danger-light px-2.5 py-2 text-body font-semibold text-danger-ink">
            <span className="mt-[7px] h-[5px] w-[5px] shrink-0 rounded-full bg-danger" />
            <span>
              <span className="mr-1.5 inline-flex rounded-chip bg-danger px-1.5 py-px align-[1px] text-[10px] font-bold uppercase tracking-[0.04em] text-white">
                Crítica
              </span>
              {a.texto}
            </span>
          </li>
        ) : (
          <li key={i} className="flex gap-2 text-body text-warn-ink">
            <span className="mt-[7px] h-[5px] w-[5px] shrink-0 rounded-full bg-warn" />
            <span>{a.texto}</span>
          </li>
        ))}
      </ul>
    </div>
  )
}

/**
 * El resumen ya no corresponde a lo que el asesor tiene delante (DEF-029).
 * Se dice en vez de mostrarlo con aplomo: QA leyó durante minutos el resumen
 * de OTRO expediente, y un borrador desactualizado es un mensaje que no viene
 * a cuento a un clic de enviarse.
 */
function AvisoActualizando({ detalle }: { detalle?: string }) {
  return (
    <p role="status" className="flex items-center gap-2 text-micro font-bold text-ink-muted">
      <span className="h-[7px] w-[7px] shrink-0 animate-pulse rounded-full bg-brand-vivid" />
      Actualizando…{detalle && <span className="font-normal">{detalle}</span>}
    </p>
  )
}

/**
 * El resumen de §9.29 que armó el agente al derivar (03-nov-2026).
 *
 * Hasta ese día era una burbuja «✦ IA» en el hilo, igual que una respuesta al
 * cliente: en PROD Ruben preguntó por qué el bot le había contestado «Nuevo
 * caso TeleAgent» a Zoila, y nunca había salido por WhatsApp. Es para el asesor,
 * y por eso vive acá.
 *
 * Plegado: lo que trae —motivo, perfil, documentos— ya está en «Qué revisar»,
 * la Ficha y Documentos. Se despliega para leerlo de corrido o copiarlo. Como
 * «Qué revisar», no depende de Bedrock: lo escribió el código.
 */
function ResumenDeLaDerivacion({ resumen }: { resumen?: ResumenDerivacion | null }) {
  const [abierto, setAbierto] = useState(false)
  if (!resumen?.texto) return null
  const hora = fechaHoraEnLima(resumen.ts)
  return (
    <div className="card-panel p-4">
      <button
        type="button"
        onClick={() => setAbierto((a) => !a)}
        aria-expanded={abierto}
        className="flex w-full items-start justify-between gap-2 text-left focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-brand"
      >
        <span>
          <span className="block text-meta font-bold uppercase tracking-[0.05em] text-ink-muted">
            Resumen de la derivación
          </span>
          <span className="mt-1 block text-body font-bold text-ink">
            {resumen.titulo || 'Derivado'}{hora ? ` · ${hora}` : ''}
          </span>
        </span>
        <span aria-hidden className="mt-0.5 text-body text-ink-faint">{abierto ? '▾' : '▸'}</span>
      </button>
      {abierto && (
        <p className="mt-2 whitespace-pre-wrap break-words text-micro text-ink">{resumen.texto}</p>
      )}
      <p className="mt-1.5 text-micro text-ink-faint">
        Lo armó el agente para el asesor. No se le envió al cliente.
      </p>
    </div>
  )
}

function TabCopiloto({ copilot, loading, updating = false, onRetry, onUseAction, readOnly = false,
                      alertas = [], resumen = null, casoId = '' }: {
  copilot: Copilot | null
  loading: boolean
  /** Lo que se muestra es de antes de lo último del caso (DEF-029). */
  updating?: boolean
  onRetry: () => void
  onUseAction: (texto: string) => void
  readOnly?: boolean
  alertas?: AlertaParaRevisar[]
  /** El resumen de §9.29 de la última derivación, o null. */
  resumen?: ResumenDerivacion | null
  /** Para que el plegado no pase de una conversación a otra. */
  casoId?: string
}) {
  const derivacion = <ResumenDeLaDerivacion key={casoId} resumen={resumen} />
  if (loading) {
    return (
      <div className="flex flex-col gap-3" aria-busy>
        <QueRevisar alertas={alertas} />
        {derivacion}
        <AvisoActualizando />
        {[0, 1, 2].map((i) => (
          <div key={i} className="card-panel space-y-2 p-4">
            <div className="h-2.5 w-1/3 animate-pulse rounded bg-surface-subtle" />
            <div className="h-3 w-full animate-pulse rounded bg-surface-subtle" />
            <div className="h-3 w-4/5 animate-pulse rounded bg-surface-subtle" />
          </div>
        ))}
      </div>
    )
  }

  if (!copilot || !copilot.disponible) {
    return (
      <div className="flex flex-col gap-3">
      <QueRevisar alertas={alertas} />
      {derivacion}
      <div className="card-panel p-4">
        <p className="text-body font-bold text-ink">Sin sugerencia</p>
        <p className="mt-1 text-micro text-ink-muted">
          {copilot?.motivo
            ? `El copiloto no pudo generar el resumen: ${copilot.motivo}.`
            : 'El copiloto no pudo generar el resumen de esta conversación.'}
          {' '}Puedes atender igual — el resumen es una ayuda, no un requisito.
        </p>
        {updating
          ? <div className="mt-2.5"><AvisoActualizando /></div>
          : <button onClick={onRetry} className="btn-row mt-2.5">Reintentar</button>}
      </div>
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-3">
      <QueRevisar alertas={alertas} />
      {derivacion}
      {updating && <AvisoActualizando detalle=" El resumen es de antes de los últimos mensajes." />}
      {/* Lo conversacional se atenúa mientras no corresponde: los hechos de
          «Qué revisar» no, porque no dependen del modelo. */}
      <div className={`flex flex-col gap-3 transition-opacity ${updating ? 'opacity-50' : ''}`}
           aria-busy={updating}>
      {/* Resumen */}
      {copilot.resumen.length > 0 && (
        <div className="card-panel p-4">
          <div className="flex items-center gap-1.5">
            <svg width="14" height="14" viewBox="0 0 17 17" fill="none">
              <path d="M8.5 1 10.2 6 15 7.7l-4.8 1.7L8.5 15l-1.7-5.6L2 7.7l4.8-1.7z"
                    fill="currentColor" className="text-brand" />
            </svg>
            <p className="text-meta font-bold uppercase tracking-[0.05em] text-ink-muted">
              Resumen de la conversación
            </p>
          </div>
          <ul className="mt-2.5 flex flex-col gap-1.5">
            {copilot.resumen.map((bullet, i) => (
              <li key={i} className="flex gap-2 text-body text-ink">
                <span className="mt-[7px] h-[5px] w-[5px] shrink-0 rounded-full bg-brand" />
                <span>{bullet}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Intención y objeción */}
      {(copilot.intencion || copilot.objecion) && (
        <div className="grid grid-cols-2 gap-3">
          <div className="card-panel p-3.5">
            <p className="text-meta font-bold uppercase tracking-[0.05em] text-ink-faint">
              Intención
            </p>
            <p className="mt-1 text-body font-bold text-ink">{copilot.intencion || '—'}</p>
          </div>
          <div className="card-panel p-3.5">
            <p className="text-meta font-bold uppercase tracking-[0.05em] text-ink-faint">
              Objeción
            </p>
            <p className="mt-1 text-body font-bold text-warn">{copilot.objecion || 'Ninguna'}</p>
          </div>
        </div>
      )}

      {/* Siguiente acción */}
      {copilot.siguiente_accion && (
        <div className="rounded-panel border border-brand-border bg-white p-4">
          <p className="text-meta font-bold uppercase tracking-[0.05em] text-brand-hover">
            Siguiente acción recomendada
          </p>
          <p className="mt-1.5 text-chat font-semibold text-ink">{copilot.siguiente_accion}</p>
          {/* Un borrador de antes de lo último no se ofrece (DEF-029). */}
          {copilot.borrador && !readOnly && !updating && (
            <button
              onClick={() => onUseAction(copilot.borrador)}
              className="mt-2.5 w-full rounded-field bg-brand-light py-2 text-micro font-bold text-brand-hover transition-colors hover:bg-brand-hoverlt"
            >
              Precargar el mensaje en el compositor
            </button>
          )}
        </div>
      )}

      {/* Alerta de speech */}
      {copilot.alerta_speech && (
        <div className="rounded-panel border border-warn-border bg-warn-light p-4">
          <p className="text-meta font-bold uppercase tracking-[0.05em] text-warn">
            Alerta de speech
          </p>
          <p className="mt-1 text-body text-warn-ink">{copilot.alerta_speech}</p>
        </div>
      )}
      </div>
    </div>
  )
}

// ── Documentos ───────────────────────────────────────────────────────────────

/**
 * El histórico de documentos —los de expedientes anteriores—, a pedido.
 *
 * DEF-017 (01-oct-2026): hasta ahí quedaban contados en
 * `documentos_anteriores` y no se podían ver. Se piden con
 * `GET /conversations/{id}/documents?alcance=anteriores` la primera vez que el
 * asesor abre «Documentos» en esa conversación, y de a 20 con «Cargar más».
 * NUNCA en el sondeo del detalle: ese corre cada 2,5 s y el historial puede ser
 * largo.
 */
interface EstadoAnteriores {
  documentos: ConversationDocument[]
  /** null mientras no se leyó la primera página. */
  total: number | null
  cursor: string | null
  cargando: boolean
  error: string | null
}

const SIN_ANTERIORES: EstadoAnteriores = {
  documentos: [], total: null, cursor: null, cargando: false, error: null,
}

function useDocumentosAnteriores(conversationId: string, activo: boolean) {
  const [estado, setEstado] = useState<EstadoAnteriores>(SIN_ANTERIORES)
  // Para qué conversación se pidió ya la primera página, y cuál está abierta
  // ahora: una respuesta que llega tarde no puede pisar la de otra conversación.
  const pedida = useRef<string | null>(null)
  const actual = useRef(conversationId)

  useEffect(() => {
    actual.current = conversationId
    pedida.current = null
    setEstado(SIN_ANTERIORES)
  }, [conversationId])

  const cargar = useCallback(async (cursor: string | null) => {
    const conv = conversationId
    setEstado((e) => ({ ...e, cargando: true, error: null }))
    try {
      const params = new URLSearchParams({ alcance: 'anteriores' })
      if (cursor) params.set('cursor', cursor)
      const res = await apiClient.get<DocumentosPorAlcance>(
        `/conversations/${conv}/documents?${params.toString()}`)
      if (actual.current !== conv) return
      setEstado((e) => ({
        documentos: cursor ? [...e.documentos, ...res.data.documents] : res.data.documents,
        total: res.data.total,
        cursor: res.data.next_cursor,
        cargando: false,
        error: null,
      }))
    } catch (err) {
      if (actual.current !== conv) return
      setEstado((e) => ({
        ...e, cargando: false,
        error: errorMessage(err, 'No se pudieron cargar los documentos anteriores.'),
      }))
    }
  }, [conversationId])

  useEffect(() => {
    if (!activo || pedida.current === conversationId) return
    pedida.current = conversationId
    void cargar(null)
  }, [activo, conversationId, cargar])

  return {
    ...estado,
    cargarMas: () => { if (estado.cursor && !estado.cargando) void cargar(estado.cursor) },
    reintentar: () => { void cargar(estado.cursor && estado.documentos.length ? estado.cursor : null) },
  }
}

type DocumentosAnteriores = ReturnType<typeof useDocumentosAnteriores>

/**
 * «Documentos»: los VIGENTES —el expediente en curso, como siempre— y aparte
 * los ANTERIORES, de expedientes que el cliente descartó al empezar de nuevo.
 *
 * DEF-017 (QA, 01-oct-2026): tras reiniciar cuatro escenarios la lista ofrecía
 * «elegir uno de los 21 que ya están en el chat». Dos cosas la saturaban: los
 * documentos de otros expedientes y los rechazados al recibirlos —un .docx, un
 * ZIP, un PDF con contraseña— pintados como tarjetas completas sin nada que
 * hacer con ellas. Los rechazados van ahora al final, en un bloque plegado.
 */
function TabDocumentos({ documentos, requeridos, anteriores, hayAnteriores = false, onReview,
                        reviewing, readOnly = false, decide = !readOnly, conversationId,
                        onDocumentoSubido, pedidoDesde = '' }: {
  documentos: ConversationDocument[]
  /** Los requisitos del checklist: de ahí sale la marca de cada tarjeta
   *  (DEF-051), la misma que la Ficha (`revision.ts`). */
  requeridos: ProgresoDocumental['requeridos']
  anteriores: DocumentosAnteriores
  /** El detalle ya vio documentos de un expediente anterior (un piso). */
  hayAnteriores?: boolean
  onReview: AlRevisar
  reviewing: string | null
  readOnly?: boolean
  /** Validar y rechazar, también en solo lectura (`puedeDecidir`). */
  decide?: boolean
  conversationId: string
  onDocumentoSubido: () => void
  /** Cuándo se generó el checklist: la hora de «pedido» de lo que no llegó. */
  pedidoDesde?: string
}) {
  const [vista, setVista] = useState<'vigentes' | 'anteriores'>('vigentes')
  // Propuesta 1a del diseño «Panel de documentos» (19-oct-2026): la pestaña
  // responde «¿qué tengo y qué falta?» POR REQUISITO, con lo que observó el
  // agente en cada intento (`documentosDelCaso.ts`). Desde el 20-oct-2026 el
  // ejecutivo decide sobre lo observado (validar igual o rechazar); lo que la
  // IA validó sigue validado. El filtro vuelve a «Todos» al cambiar.
  const [filtro, setFiltro] = useState<FiltroDeRequisitos>('todos')
  useEffect(() => { setFiltro('todos') }, [conversationId])
  const { tarjetas, rechazados } = separarRechazados(documentos)
  const caso = agruparPorRequisito(tarjetas, requeridos, pedidoDesde)
  const grupos = gruposDelFiltro(caso.grupos, filtro)
  const conChecklist = caso.aplicables > 0
  const tarjeta = (doc: ConversationDocument) => (
    <DocumentoCard
      key={doc.doc_id}
      doc={doc}
      version={etiquetaDeVersion(doc, documentos)}
      marca={marcaDelDocumento(doc.doc_id, requeridos)}
      onReview={onReview}
      busy={reviewing === doc.doc_id}
      readOnly={readOnly}
      decide={decide}
    />
  )
  const conAnteriores = hayAnteriores || (anteriores.total ?? 0) > 0
  // Validar o rechazar un archivo: el ejecutivo en su bandeja y supervisión
  // en En vivo. Sin eso, nada.
  const decision = decide ? { onReview, reviewing } : null

  // En «En vivo» el supervisor mira sin escribir: subir es una acción, y las
  // acciones no se ofrecen en solo lectura. Para «traer del chat» se ofrecen
  // solo los que se pueden volver a procesar: un ZIP rechazado no.
  const subir = readOnly ? null : (
    <SubirDocumento conversationId={conversationId} onSubido={onDocumentoSubido} documentos={tarjetas} />
  )

  const etiquetaAnteriores = anteriores.total !== null
    ? `Anteriores (${anteriores.total})`
    : anteriores.cargando ? 'Anteriores (…)' : 'Anteriores'

  return (
    <div className="flex flex-col gap-3">
      <div className="flex gap-1 rounded-field border border-surface-border bg-white p-1" role="tablist">
        {([['vigentes', `Vigentes (${documentos.length})`], ['anteriores', etiquetaAnteriores]] as const)
          .map(([clave, texto]) => (
            <button
              key={clave}
              role="tab"
              aria-selected={vista === clave}
              onClick={() => setVista(clave)}
              className={`flex-1 rounded-field px-2 py-1.5 text-micro font-bold transition-colors ${
                vista === clave ? 'bg-brand-light text-brand-hover' : 'text-ink-faint hover:text-ink-muted'
              }`}
            >
              {texto}
            </button>
          ))}
      </div>

      {vista === 'vigentes' ? (
        <>
          {conAnteriores && (
            <p className="text-micro text-ink-muted">
              Se muestran solo los documentos del expediente actual. Los de un expediente
              anterior están en «Anteriores».
            </p>
          )}
          {documentos.length === 0 && (
            <div className="card-panel p-4">
              <p className="text-body font-bold text-ink">Sin documentos</p>
              <p className="mt-1 text-micro text-ink-muted">
                {conAnteriores
                  ? 'El cliente todavía no envió documentos para este expediente.'
                  : 'El cliente todavía no envió ningún documento por WhatsApp.'}
              </p>
            </div>
          )}
          {conChecklist && (
            <>
              <ResumenDelExpediente validados={caso.validados} observados={caso.observados}
                                    rechazados={caso.rechazados} sinRecibir={caso.sinRecibir}
                                    descargables={paraDescargar(caso.descargables)} />
              <FiltroDeRequisitos filtro={filtro} onCambiar={setFiltro} cuenta={{
                todos: caso.aplicables, observado: caso.observados, validado: caso.validados,
                rechazado: caso.rechazados, sin_recibir: caso.sinRecibir }} />
              {/* El orden lo pidió Diego Casallo (20-oct-2026): validados, sin
                  recibir, observados, rechazados y al final «Otros archivos»
                  (`ORDEN_DE_SECCIONES`). */}
              {seccionesDeDocumentos(grupos).map(({ seccion, grupos: deLaSeccion }) => {
                if (seccion === 'validado') return (
                  <SeccionDeGrupos key={seccion} titulo="Validados · listos para descargar" tono="ok"
                                   grupos={deLaSeccion}
                                   render={(g) => (
                                     <GrupoDelArchivo key={g.clave} grupo={g} detalle={tarjeta}
                                                      conversationId={conversationId} />
                                   )} />
                )
                if (seccion === 'sin_recibir') return (
                  <Fragment key={seccion}>
                    <EtiquetaDeSeccion tono="neutro">Sin recibir</EtiquetaDeSeccion>
                    <ListaSinRecibir grupos={deLaSeccion} />
                  </Fragment>
                )
                if (seccion === 'observado') return (
                  <SeccionDeGrupos key={seccion} titulo="Observados por el agente IA" tono="aviso"
                                   grupos={deLaSeccion}
                                   render={(g, i) => (
                                     <GrupoObservado key={g.clave} grupo={g} abiertoAlInicio={i === 0}
                                                     detalle={tarjeta} conversationId={conversationId}
                                                     decision={decision} />
                                   )} />
                )
                return (
                  <SeccionDeGrupos key={seccion} titulo="Rechazados" tono="mal" grupos={deLaSeccion}
                                   render={(g) => (
                                     <GrupoRechazado key={g.clave} grupo={g} detalle={tarjeta}
                                                     onReview={onReview} readOnly={readOnly} />
                                   )} />
                )
              })}
            </>
          )}
          {/* Lo que no quedó atado a ningún requisito —ilegible, de más, de
              antes de que se registrara el veredicto— con su tarjeta entera:
              ahí están «Aprobar» y «Rechazar» de lo que espera revisión. */}
          {(filtro === 'todos' || !conChecklist) && caso.otros.length > 0 && (
            <>
              {conChecklist && <EtiquetaDeSeccion tono="neutro">Otros archivos</EtiquetaDeSeccion>}
              {caso.otros.map((d) => (
                <Fragment key={d.doc_id}>
                  {tarjeta(d)}
                  {/* DEF-109: lo aprobado sin requisito no entra en «Descargar
                      validados»; se baja desde acá, con su nombre de §9.31. */}
                  {d.status === 'APPROVED' && (
                    <div className="-mt-1 flex justify-end" data-descarga-aprobado={d.doc_id}>
                      <BotonDescargar docId={d.doc_id} compacto />
                    </div>
                  )}
                </Fragment>
              ))}
            </>
          )}
          {subir}
          {rechazados.length > 0 && <BloqueRechazados documentos={rechazados} />}
        </>
      ) : (
        <ListaAnteriores estado={anteriores} />
      )}
    </div>
  )
}

/** Fecha y hora cortas, en hora de Lima. */
function cuando(ts: string): string {
  if (!ts) return ''
  return new Date(ts).toLocaleString('es-PE', {
    timeZone: 'America/Lima', day: '2-digit', month: '2-digit',
    hour: '2-digit', minute: '2-digit',
  })
}

/**
 * Lo que la guarda rechazó al recibirlo, una línea por archivo y plegado por
 * defecto: no hay nada que revisar ahí, pero el asesor tiene que poder ver qué
 * mandó el cliente y por qué no entró.
 */
function BloqueRechazados({ documentos }: { documentos: ConversationDocument[] }) {
  const [abierto, setAbierto] = useState(false)
  return (
    <div className="card-panel overflow-hidden">
      <button
        onClick={() => setAbierto((v) => !v)}
        aria-expanded={abierto}
        className="flex w-full items-center justify-between px-3.5 py-2.5 text-left text-micro font-bold text-ink-muted hover:bg-surface-alt"
      >
        <span>Rechazados al recibirlos ({documentos.length})</span>
        <span aria-hidden>{abierto ? '▾' : '▸'}</span>
      </button>
      {abierto && (
        <ul className="flex flex-col divide-y divide-surface-faint border-t border-surface-faint">
          {documentos.map((doc) => (
            <li key={doc.doc_id} className="flex items-center gap-2 px-3.5 py-2">
              <div className="min-w-0 flex-1">
                <p className="truncate text-micro font-bold text-ink">{doc.filename || 'archivo'}</p>
                <p className="truncate text-meta text-ink-muted">
                  {doc.motivo_rechazo || 'Rechazado al recibirlo'}
                  {doc.created_at && ` · ${cuando(doc.created_at)}`}
                </p>
              </div>
              <BotonVerDocumento docId={doc.doc_id} filename={doc.filename} />
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

/** Un documento anterior en una línea: el histórico se consulta, no se revisa. */
function FilaDocumento({ doc }: { doc: ConversationDocument }) {
  // Un anterior sin veredicto registrado no lleva chip: con un historial de
  // noventa archivos, noventa «sin veredicto» taparían los que sí lo tienen.
  const lectura = doc.veredicto_agente && doc.veredicto_agente.resultado !== SIN_VEREDICTO
    ? lecturaDelVeredicto(doc) : null
  return (
    <li className="flex items-center gap-2 px-3.5 py-2">
      <div className="min-w-0 flex-1">
        <p className="truncate text-micro font-bold text-ink">{doc.filename || doc.type}</p>
        <p className="flex flex-wrap items-center gap-1 text-meta text-ink-muted">
          <span>{doc.type || 'Sin clasificar'}</span>
          {doc.created_at && <span>· {cuando(doc.created_at)}</span>}
          <StatusPill status={doc.status} kind="document" />
          {lectura && (
            <span className={`inline-flex items-center rounded-chip px-1.5 py-px text-pill font-bold ${TONO_CHIP[lectura.tono]}`}
                  title={[lectura.titulo, lectura.motivo].filter((x) => x).join(' · ')}>
              {lectura.chip}
            </span>
          )}
        </p>
      </div>
      <BotonVerDocumento docId={doc.doc_id} filename={doc.filename} />
    </li>
  )
}

/**
 * Los anteriores agrupados por día (de Lima), el más reciente abierto y el
 * resto plegado: un historial de semanas desplegado entero es la lista
 * saturada que se vino a arreglar. «Cargar más» trae la página siguiente.
 */
function ListaAnteriores({ estado }: { estado: DocumentosAnteriores }) {
  // Los días que el asesor abrió o cerró a mano. El más reciente arranca
  // abierto: alternarlo lo cierra.
  const [alternados, setAlternados] = useState<Set<string>>(new Set())
  const grupos = agruparPorDia(estado.documentos)

  if (estado.total === null && estado.cargando) {
    return <p className="text-micro text-ink-muted">Cargando documentos anteriores…</p>
  }
  if (estado.error && estado.documentos.length === 0) {
    return (
      <div className="card-panel p-4">
        <p className="text-micro text-danger">{estado.error}</p>
        <button onClick={estado.reintentar} className="btn-row mt-2">Reintentar</button>
      </div>
    )
  }
  if (estado.total === 0) {
    return (
      <div className="card-panel p-4">
        <p className="text-body font-bold text-ink">Sin documentos anteriores</p>
        <p className="mt-1 text-micro text-ink-muted">
          Todo lo que mandó el cliente es del expediente actual.
        </p>
      </div>
    )
  }

  const alternar = (dia: string) => setAlternados((s) => {
    const nuevo = new Set(s)
    if (nuevo.has(dia)) nuevo.delete(dia)
    else nuevo.add(dia)
    return nuevo
  })

  return (
    <div className="flex flex-col gap-2">
      {grupos.map((g, i) => {
        const abierto = (i === 0) !== alternados.has(g.dia)
        const { tarjetas, rechazados } = separarRechazados(g.documentos)
        return (
          <div key={g.dia || 'sin-fecha'} className="card-panel overflow-hidden">
            <button
              onClick={() => alternar(g.dia)}
              aria-expanded={abierto}
              className="flex w-full items-center justify-between px-3.5 py-2.5 text-left hover:bg-surface-alt"
            >
              <span className="text-micro font-bold capitalize text-ink">{etiquetaDelDia(g.dia)}</span>
              <span className="text-meta text-ink-faint">
                {g.documentos.length} {g.documentos.length === 1 ? 'documento' : 'documentos'}
                {' '}{abierto ? '▾' : '▸'}
              </span>
            </button>
            {abierto && (
              <div className="border-t border-surface-faint">
                {tarjetas.length > 0 && (
                  <ul className="flex flex-col divide-y divide-surface-faint">
                    {tarjetas.map((doc) => <FilaDocumento key={doc.doc_id} doc={doc} />)}
                  </ul>
                )}
                {rechazados.length > 0 && (
                  <div className="p-2"><BloqueRechazados documentos={rechazados} /></div>
                )}
              </div>
            )}
          </div>
        )
      })}

      {estado.error && estado.documentos.length > 0 && (
        <p className="text-micro text-danger">{estado.error}</p>
      )}
      {estado.cursor && (
        <button onClick={estado.cargarMas} disabled={estado.cargando} className="btn-row self-center disabled:opacity-50">
          {estado.cargando ? 'Cargando…' : 'Cargar más'}
        </button>
      )}
    </div>
  )
}

/**
 * Subir un documento del caso sin salir de la conversación.
 *
 * El archivo va al MISMO `incoming/` de S3 por el que entran los del cliente,
 * así que lo recoge el pipeline de siempre: clasifica, extrae campos, archiva
 * en la carpeta de §9.31 y marca el requisito del checklist. El asesor no toca
 * Drive ni crea carpetas — que es justamente lo que este control viene a
 * ahorrarle.
 *
 * Lo único distinto de un documento del cliente es que **el bot no le contesta**:
 * el backend marca el archivo con `origen: asesor` y el flujo lo anota en
 * silencio. Agradecerle al cliente un documento que no envió lo confunde, y más
 * si está esperando a una persona.
 *
 * La subida va del navegador a S3 con una URL prefirmada. Un PDF de 3 MB en
 * base64 son 4 MB de JSON y API Gateway corta en 10.
 */
function SubirDocumento({ conversationId, onSubido, documentos }: {
  conversationId: string
  onSubido: () => void
  documentos: ConversationDocument[]
}) {
  const [subiendo, setSubiendo] = useState(false)
  const [trayendo, setTrayendo] = useState<string | null>(null)
  const [verChat, setVerChat] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  const ocupado = subiendo || trayendo !== null

  async function subir(file: File) {
    setSubiendo(true)
    setError(null)
    try {
      const { data } = await apiClient.post<{ upload_url: string }>(
        `/conversations/${conversationId}/documents`,
        { filename: file.name, mime_type: file.type, size: file.size },
      )
      // `fetch` y no `apiClient`: la URL prefirmada ya lleva la autorización en
      // la firma, y mandarle el token del portal la invalida.
      const res = await fetch(data.upload_url, {
        method: 'PUT',
        headers: { 'Content-Type': file.type },
        body: file,
      })
      if (!res.ok) throw new Error(`S3 respondió ${res.status}`)
      onSubido()
    } catch (err) {
      setError(errorMessage(err, 'No se pudo subir el documento.'))
    } finally {
      setSubiendo(false)
      if (inputRef.current) inputRef.current.value = ''
    }
  }

  // El archivo ya está en S3: el backend lo copia dentro de `incoming/` y el
  // trigger de siempre hace el resto. No se sube nada desde el navegador.
  async function traerDelChat(doc: ConversationDocument) {
    setTrayendo(doc.doc_id)
    setError(null)
    try {
      await apiClient.post(`/conversations/${conversationId}/documents`,
                           { source_document_id: doc.doc_id })
      setVerChat(false)
      onSubido()
    } catch (err) {
      setError(errorMessage(err, 'No se pudo traer el documento.'))
    } finally {
      setTrayendo(null)
    }
  }

  return (
    <div className="card-panel p-3.5">
      <input
        ref={inputRef}
        type="file"
        accept="application/pdf,image/jpeg,image/png,image/webp"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0]
          if (file) void subir(file)
        }}
      />
      <button
        type="button"
        disabled={ocupado}
        onClick={() => inputRef.current?.click()}
        className="flex w-full items-center justify-center gap-2 rounded-field border border-dashed border-surface-border bg-surface-subtle px-3 py-2.5 text-body font-bold text-ink transition hover:bg-surface disabled:opacity-50"
      >
        {subiendo ? 'Subiendo…' : '+ Subir documento del caso'}
      </button>

      {/* El caso común no es que el asesor tenga el archivo en su máquina: es
          que el cliente ya lo mandó por WhatsApp. Bajarlo y volver a subirlo es
          justo el paseo que este apartado vino a eliminar. */}
      {documentos.length > 0 && (
        <button
          type="button"
          disabled={ocupado}
          onClick={() => setVerChat((v) => !v)}
          className="mt-2 w-full text-center text-micro font-bold text-brand-hover transition hover:underline disabled:opacity-50"
        >
          {verChat
            ? 'Cerrar'
            : `o elegir uno de los ${documentos.length} que ya están en el chat`}
        </button>
      )}

      {verChat && (
        <ul className="mt-2 flex flex-col gap-1.5">
          {documentos.map((doc) => (
            <li
              key={doc.doc_id}
              className="flex items-center gap-2 rounded-field border border-surface-border bg-surface-subtle px-2.5 py-2"
            >
              <div className="min-w-0 flex-1">
                <p className="truncate text-micro font-bold text-ink">{doc.filename}</p>
                <p className="truncate text-micro text-ink-muted">
                  {doc.type || 'Sin clasificar'}
                  {doc.drive_url ? ' · ya está en la carpeta' : ''}
                </p>
              </div>
              <button
                type="button"
                disabled={ocupado}
                onClick={() => void traerDelChat(doc)}
                className="shrink-0 rounded-field border border-surface-border bg-surface px-2.5 py-1 text-micro font-bold text-ink transition hover:bg-surface-subtle disabled:opacity-50"
              >
                {trayendo === doc.doc_id ? 'Trayendo…' : 'Traer'}
              </button>
            </li>
          ))}
        </ul>
      )}

      <p className="mt-2 text-micro text-ink-muted">
        Se clasifica y se archiva solo en la carpeta del cliente. El cliente no
        recibe ningún mensaje.
      </p>
      {error && <p className="mt-2 text-micro text-danger">{error}</p>}
    </div>
  )
}

/** Los colores de cada tono, con los tokens de la paleta. */
const TONO_CHIP: Record<Tono, string> = {
  ok: 'bg-brand-light text-brand-hover',
  aviso: 'border border-warn-border bg-warn-light text-warn',
  mal: 'border border-danger-border bg-danger-light text-danger',
  info: 'bg-info-light text-info',
  neutro: 'bg-surface-subtle text-ink-faint',
}
const TONO_BLOQUE: Record<Tono, string> = {
  ok: 'border-brand-light bg-brand-light',
  aviso: 'border-warn-border bg-warn-light',
  mal: 'border-danger-border bg-danger-light',
  info: 'border-info-light bg-info-light',
  neutro: 'border-surface-border bg-surface-subtle',
}
const TONO_TITULO: Record<Tono, string> = {
  ok: 'text-brand-hover',
  aviso: 'text-warn-ink',
  mal: 'text-danger-ink',
  info: 'text-info',
  neutro: 'text-ink-muted',
}

// ── Propuesta 1a y decisión del ejecutivo: los documentos por requisito ────

const FILTROS_DE_REQUISITOS: Array<{ clave: FiltroDeRequisitos; texto: string }> = [
  { clave: 'todos', texto: 'Todos' },
  { clave: 'observado', texto: 'Observados' },
  { clave: 'validado', texto: 'Validados' },
  { clave: 'rechazado', texto: 'Rechazados' },
  { clave: 'sin_recibir', texto: 'Sin recibir' },
]

/** Los chips del diseño: cuentan REQUISITOS, no archivos. */
function FiltroDeRequisitos({ filtro, cuenta, onCambiar }: {
  filtro: FiltroDeRequisitos
  cuenta: Record<FiltroDeRequisitos, number>
  onCambiar: (f: FiltroDeRequisitos) => void
}) {
  return (
    <div className="flex flex-wrap gap-1.5" role="group" aria-label="Filtrar por estado del requisito">
      {FILTROS_DE_REQUISITOS.map(({ clave, texto }) => (
        <button key={clave} type="button" aria-pressed={filtro === clave}
                onClick={() => onCambiar(clave)}
                className={filtro === clave ? 'pill-filter-active' : 'pill-filter'}>
          {texto} ({cuenta[clave]})
        </button>
      ))}
    </div>
  )
}

/** Lo que baja «Descargar validados»: cada archivo con el nombre de su requisito. */
function paraDescargar(descargables: Array<{ doc: ConversationDocument; nombre: string }>) {
  return descargables.map(({ doc, nombre }) => ({ docId: doc.doc_id, nombre }))
}

/** «Validados / Observados / Rechazados / Sin recibir» y el botón que baja lo validado. */
function ResumenDelExpediente({ validados, observados, rechazados, sinRecibir, descargables }: {
  validados: number
  observados: number
  rechazados: number
  sinRecibir: number
  descargables: Array<{ docId: string; nombre: string }>
}) {
  const fichas: Array<{ n: number; texto: string; fondo: string; cifra: string; rotulo: string }> = [
    { n: validados, texto: validados === 1 ? 'Validado' : 'Validados',
      fondo: 'bg-brand-light', cifra: 'text-brand', rotulo: 'text-brand-ink' },
    { n: observados, texto: observados === 1 ? 'Observado' : 'Observados',
      fondo: 'bg-warn-light', cifra: 'text-warn', rotulo: 'text-warn-ink' },
    { n: rechazados, texto: rechazados === 1 ? 'Rechazado' : 'Rechazados',
      fondo: 'bg-danger-light', cifra: 'text-danger', rotulo: 'text-danger-ink' },
    { n: sinRecibir, texto: 'Sin recibir',
      fondo: 'bg-surface-subtle', cifra: 'text-ink-muted', rotulo: 'text-ink-muted' },
  ]
  return (
    <div className="card-panel flex flex-col gap-3 p-3.5">
      <div className="grid grid-cols-4 gap-1.5">
        {fichas.map((f) => (
          <div key={f.texto} className={`rounded-btn px-2 py-2 ${f.fondo}`}>
            <p className={`text-[20px] font-extrabold leading-[1.1] ${f.cifra}`}>{f.n}</p>
            <p className={`text-meta font-bold leading-tight ${f.rotulo}`}>{f.texto}</p>
          </div>
        ))}
      </div>
      <BotonDescargarValidados archivos={descargables} variante="principal" />
    </div>
  )
}

/**
 * «↓ Descargar validados (n)»: lo validado por la IA o por un ejecutivo, uno
 * tras otro y con el nombre del requisito. El navegador pregunta una vez si se
 * permiten varias descargas.
 */
function BotonDescargarValidados({ archivos, variante }: {
  archivos: Array<{ docId: string; nombre: string }>
  variante: 'principal' | 'suave'
}) {
  const [bajando, setBajando] = useState(false)
  const [error, setError] = useState<string | null>(null)
  async function bajar() {
    setBajando(true)
    setError(null)
    const fallidos = await descargarVarios(archivos)
    setBajando(false)
    if (fallidos.length) {
      setError(`No se pudo bajar ${fallidos.length} de ${archivos.length}. Vuelve a intentarlo o usa «Abrir en Drive».`)
    }
  }
  return (
    <div className="flex flex-col gap-1">
      <button type="button" disabled={bajando || archivos.length === 0} onClick={() => void bajar()}
              className={`flex items-center justify-center gap-2 rounded-btn font-bold transition-colors disabled:opacity-50 ${
                variante === 'principal'
                  ? 'bg-brand px-3.5 py-[9px] text-chat text-white hover:bg-brand-hover'
                  : 'border border-brand-border bg-brand-light px-3.5 py-2 text-body text-brand-hover hover:bg-brand-hoverlt'}`}>
        {bajando ? 'Bajando…' : `↓ Descargar validados (${archivos.length})`}
      </button>
      {error && <p className="text-meta text-danger">{error}</p>}
    </div>
  )
}

/** «↓ Descargar» de UN archivo validado, con el nombre de su requisito. */
function BotonDescargar({ docId, nombre = '', compacto = false }: {
  docId: string
  nombre?: string
  compacto?: boolean
}) {
  const [bajando, setBajando] = useState(false)
  const [error, setError] = useState(false)
  async function bajar() {
    setBajando(true)
    setError(false)
    try {
      await descargarDocumento(docId, nombre)
    } catch {
      setError(true)
    } finally {
      setBajando(false)
    }
  }
  return (
    <button type="button" disabled={bajando} onClick={() => void bajar()}
            title={error ? 'No se pudo bajar: vuelve a intentarlo o usa «Abrir en Drive»' : undefined}
            className={`shrink-0 rounded-field border font-bold transition-colors disabled:opacity-50 ${
              error ? 'border-danger-border bg-danger-light text-danger'
                : 'border-brand-border bg-brand-light text-brand-hover hover:bg-brand-hoverlt'} ${
              compacto ? 'px-[9px] py-1 text-[11px]' : 'px-[11px] py-1.5 text-[11px]'}`}>
      {bajando ? 'Bajando…' : error ? 'Reintentar' : '↓ Descargar'}
    </button>
  )
}

type TonoDeSeccion = 'ok' | 'aviso' | 'mal' | 'neutro'

const COLOR_DE_SECCION: Record<TonoDeSeccion, string> = {
  ok: 'text-brand-hover', aviso: 'text-warn', mal: 'text-danger', neutro: 'text-ink-faint',
}

function EtiquetaDeSeccion({ tono, children }: { tono: TonoDeSeccion; children: ReactNode }) {
  return (
    <p className={`text-[10px] font-bold uppercase tracking-[0.06em] ${COLOR_DE_SECCION[tono]}`}>
      {children}
    </p>
  )
}

function SeccionDeGrupos({ titulo, tono, grupos, render }: {
  titulo: string
  tono: TonoDeSeccion
  grupos: Array<GrupoDelRequisito<ConversationDocument>>
  render: (g: GrupoDelRequisito<ConversationDocument>, i: number) => ReactNode
}) {
  if (grupos.length === 0) return null
  return (
    <>
      <EtiquetaDeSeccion tono={tono}>{titulo}</EtiquetaDeSeccion>
      {grupos.map(render)}
    </>
  )
}

/** Lo que el panel necesita para que el ejecutivo decida, o null en solo lectura. */
type Decision = { onReview: AlRevisar; reviewing: string | null } | null

/**
 * Validar o rechazar un archivo: decisiones DEFINITIVAS del ejecutivo, con
 * confirmación (Diego Casallo y Ruben, 20-oct-2026: sin «deshacer»).
 * «Validar igual» sobre lo que la IA observó muestra qué objetó; rechazar pide
 * el motivo, que viaja al revisor como `notes` y queda en el archivo. El
 * revisor no cambia una decisión tomada (409).
 */
function DecisionDelEjecutivo({ archivo, observacionIA = '', sugerido = '', busy, conVer = true,
                                onValidar, onRechazar }: {
  archivo: ConversationDocument
  /** Lo que objetó la IA: con esto el botón es «Validar igual». */
  observacionIA?: string
  /** El motivo de rechazo que se preselecciona (`motivoSugerido`). */
  sugerido?: string
  busy: boolean
  conVer?: boolean
  onValidar: () => void
  onRechazar: (motivo: string) => void
}) {
  const [modo, setModo] = useState<null | 'validar' | 'rechazar'>(null)
  const [motivo, setMotivo] = useState('')
  useEffect(() => { setModo(null); setMotivo('') }, [archivo.doc_id])
  const igual = Boolean(observacionIA)

  if (modo === 'validar') {
    return (
      <div role="alertdialog" aria-label="Confirmar la validación"
           className="flex flex-col gap-2 rounded-btn border border-warn-border bg-white p-2.5">
        <p className="text-micro leading-[1.4] text-ink-soft">
          {igual
            ? <><b>La IA lo observó:</b> {observacionIA} ¿Lo validas igual?</>
            : <>¿Validas <b>{archivo.filename || 'este documento'}</b>?</>}
          {' '}Queda registrado a tu nombre y no se puede deshacer.
        </p>
        <div className="flex gap-1.5">
          <button type="button" disabled={busy}
                  onClick={() => { onValidar(); setModo(null) }}
                  className="rounded-field bg-brand px-[11px] py-1.5 text-[11px] font-bold text-white hover:bg-brand-hover disabled:opacity-50">
            Sí, validar
          </button>
          <button type="button" onClick={() => setModo(null)} className="btn-row">Cancelar</button>
        </div>
      </div>
    )
  }
  if (modo === 'rechazar') {
    return (
      <div role="alertdialog" aria-label="Rechazar el documento"
           className="flex flex-col gap-2 rounded-btn border border-danger-border bg-white p-2.5">
        <p className="text-micro font-bold text-danger-ink">¿Por qué lo rechazas?</p>
        <div className="flex flex-wrap gap-[5px]">
          {MOTIVOS_DE_RECHAZO.map((m) => (
            <button key={m} type="button" aria-pressed={motivo === m} onClick={() => setMotivo(m)}
                    className={`rounded-chip border px-[9px] py-1 text-[11px] font-semibold ${
                      motivo === m ? 'border-danger bg-danger-light text-danger-ink'
                        : 'border-surface-border bg-white text-ink-muted'}`}>
              {m}
            </button>
          ))}
        </div>
        <p className="text-meta text-ink-faint">
          No se puede deshacer. El cliente no se entera hasta que se lo pidas de nuevo.
        </p>
        <div className="flex gap-1.5">
          <button type="button" disabled={busy || !motivo}
                  onClick={() => { onRechazar(motivo); setModo(null) }}
                  className={`rounded-field px-[11px] py-1.5 text-[11px] font-bold text-white disabled:cursor-not-allowed ${
                    motivo ? 'bg-danger' : 'bg-danger-border'}`}>
            Rechazar documento
          </button>
          <button type="button" onClick={() => setModo(null)} className="btn-row">Cancelar</button>
        </div>
      </div>
    )
  }
  return (
    <div className="flex flex-wrap gap-1.5">
      {conVer && <BotonVerDocumento docId={archivo.doc_id} filename={archivo.filename} compacto="boton" />}
      <button type="button" disabled={busy} onClick={() => setModo('validar')}
              className="btn-soft disabled:opacity-50">
        {busy ? 'Guardando…' : igual ? '✓ Validar igual' : '✓ Validar'}
      </button>
      <button type="button" disabled={busy} onClick={() => { setMotivo(sugerido); setModo('rechazar') }}
              className="btn-danger disabled:opacity-50">
        Rechazar
      </button>
    </div>
  )
}

/**
 * Después de rechazar: el cliente no lo sabe hasta que se le pide de nuevo
 * (DEF-044). Si la IA ya le había pedido el reenvío por ese archivo, sí lo
 * sabe, y el pedido es opcional.
 */
function AvisoDeRechazo({ docId, onReview, clienteAvisado = false }: {
  docId: string
  onReview: AlRevisar
  clienteAvisado?: boolean
}) {
  return (
    <div className="flex flex-col gap-1.5 rounded-btn border border-warn-border bg-warn-light px-2.5 py-2">
      <p className="text-[11px] font-bold text-warn-ink">
        {clienteAvisado
          ? 'La IA ya le pidió al cliente que lo reenvíe.'
          : 'El cliente todavía no sabe que lo rechazaste.'}
      </p>
      <div>
        <button type="button" onClick={() => onReview(docId, 'request')} className="btn-soft">
          ✉ Pedir de nuevo al cliente
        </button>
      </div>
    </div>
  )
}

/**
 * Un intento: el archivo, por qué lo observó el agente y cuándo. «Ver» abre
 * el visor; «▸» despliega la tarjeta entera del archivo —campos, versión y las
 * acciones del asesor—.
 */
function FilaDeIntento({ fila, detalle, ultimaSinBorde = false }: {
  fila: ArchivoDelRequisito<ConversationDocument>
  detalle: (doc: ConversationDocument) => ReactNode
  ultimaSinBorde?: boolean
}) {
  const [abierta, setAbierta] = useState(false)
  return (
    <div className={ultimaSinBorde ? '' : 'border-b border-surface-faint'}>
      <div className="flex items-start gap-2.5 px-3.5 py-[9px]">
        <span className="mt-px flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-surface-subtle text-[10px] font-extrabold text-ink-muted">
          {fila.n}
        </span>
        <div className="min-w-0 flex-1">
          <p className="truncate text-micro font-bold text-ink" title={fila.doc.filename}>
            {fila.doc.filename || fila.doc.type}
          </p>
          <p className="text-meta leading-[1.35] text-ink-muted">
            {[fila.motivo, fila.hora].filter((x) => x).join(' · ')}
          </p>
        </div>
        <BotonVerDocumento docId={fila.doc.doc_id} filename={fila.doc.filename} compacto="borde" />
        <button type="button" onClick={() => setAbierta((v) => !v)} aria-expanded={abierta}
                aria-label="Detalle del archivo" title="Detalle del archivo"
                className="shrink-0 px-1 pt-1 text-[11px] text-ink-faint hover:text-ink">
          {abierta ? '▾' : '▸'}
        </button>
      </div>
      {abierta && <div className="bg-surface-alt px-3 pb-3">{detalle(fila.doc)}</div>}
    </div>
  )
}

/** Un requisito que la IA observó: la última observación, cada intento, lo
 *  que leyó el cliente y la decisión del ejecutivo («Validar igual»). */
function GrupoObservado({ grupo, abiertoAlInicio, detalle, conversationId, decision }: {
  grupo: GrupoDelRequisito<ConversationDocument>
  abiertoAlInicio: boolean
  detalle: (doc: ConversationDocument) => ReactNode
  conversationId: string
  decision: Decision
}) {
  const [mensajeAbierto, setMensajeAbierto] = useState(abiertoAlInicio)
  useEffect(() => { setMensajeAbierto(abiertoAlInicio) }, [conversationId, abiertoAlInicio])
  const doc = grupo.aDecidir
  return (
    <div className="overflow-hidden rounded-panel border border-warn-border bg-white" data-requisito="observado">
      <div className="flex flex-col gap-2 px-3.5 py-3">
        <div className="flex items-start justify-between gap-2">
          <p className="text-body font-bold leading-[1.35] text-ink">{grupo.label}</p>
          <span className="badge-amber shrink-0">⚠ {grupo.intentosTexto || 'Observado'}</span>
        </div>
        <div className="rounded-field bg-warn-light px-2.5 py-2">
          <p className="text-[10px] font-bold uppercase tracking-[0.05em] text-warn">
            Última observación{grupo.hora ? ` · ${grupo.hora}` : ''}
          </p>
          <p className="mt-0.5 text-[12px] font-semibold leading-[1.4] text-warn-ink">
            {grupo.observacionIA || 'El agente no registró el motivo.'}
          </p>
        </div>
        {grupo.candidatos.length > 0 && (
          <p className="text-meta text-ink-muted">
            No quedó registrado qué archivo observó la IA: los {grupo.candidatos.length} de este
            tipo están en «Otros archivos», para validarlos o rechazarlos.
          </p>
        )}
        {doc && decision && (
          <DecisionDelEjecutivo archivo={doc} observacionIA={grupo.observacionIA || grupo.sub}
                                sugerido={grupo.motivoSugerido} busy={decision.reviewing === doc.doc_id}
                                conVer={false}
                                onValidar={() => decision.onReview(doc.doc_id, 'approve')}
                                onRechazar={(motivo) => decision.onReview(doc.doc_id, 'reject', motivo)} />
        )}
      </div>
      {grupo.archivos.length > 0 && (
        <div className="border-t border-surface-faint">
          {grupo.archivos.map((fila, i) => (
            <FilaDeIntento key={fila.doc.doc_id} fila={fila} detalle={detalle}
                           ultimaSinBorde={!grupo.mensaje && i === grupo.archivos.length - 1} />
          ))}
        </div>
      )}
      {grupo.mensaje && (
        <>
          <button type="button" onClick={() => setMensajeAbierto((v) => !v)} aria-expanded={mensajeAbierto}
                  className="flex w-full items-center justify-between bg-surface-alt px-3.5 py-[9px] text-left text-[11px] font-bold text-ink-muted">
            <span>Mensaje que recibió el cliente</span>
            <span aria-hidden>{mensajeAbierto ? '▾' : '▸'}</span>
          </button>
          {mensajeAbierto && (
            <div className="bg-surface-alt px-3.5 pb-3">
              <p className="rounded-btn bg-purple-light px-2.5 py-2 text-micro leading-[1.45] text-ink-soft">
                {grupo.mensaje.texto}
                <span className="mt-1 block text-[10px] font-bold text-purple">
                  ✦ IA{grupo.mensaje.hora ? ` · enviado ${grupo.mensaje.hora}` : ''}
                </span>
              </p>
            </div>
          )}
        </>
      )}
    </div>
  )
}

/**
 * Un requisito validado —por la IA o por una persona— con el archivo que lo
 * cumple, listo para bajar. Rechazarlo igual se puede, desde «Detalle»: la
 * tarjeta entera del archivo.
 */
function GrupoDelArchivo({ grupo, detalle, conversationId }: {
  grupo: GrupoDelRequisito<ConversationDocument>
  detalle: (doc: ConversationDocument) => ReactNode
  conversationId: string
}) {
  const [verDetalle, setVerDetalle] = useState(false)
  const [verAnteriores, setVerAnteriores] = useState(false)
  useEffect(() => { setVerDetalle(false); setVerAnteriores(false) }, [conversationId])
  const doc = grupo.archivo
  return (
    <div className="overflow-hidden rounded-panel border border-brand-border bg-white" data-requisito="validado"
         data-validado-por={grupo.validadoPor}>
      <div className="flex gap-3 px-3.5 py-3">
        <div className="flex h-[50px] w-10 shrink-0 items-center justify-center rounded-field border border-brand-border bg-brand-light text-base font-extrabold text-brand">
          ✓
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-body font-bold text-ink">{grupo.label}</p>
          <p className="break-words text-meta leading-[1.35] text-brand-ink">{grupo.sub}</p>
        </div>
      </div>
      {grupo.observaciones.length > 0 && (
        <ul className="flex flex-col gap-0.5 px-3.5 pb-2.5">
          {grupo.observaciones.map((o) => (
            <li key={o} className="text-meta text-warn-ink">⚠ {o}</li>
          ))}
        </ul>
      )}
      {doc && (
        <div className="flex flex-wrap items-center gap-1.5 border-t border-surface-faint px-3.5 py-2.5">
          <BotonDescargar docId={doc.doc_id} nombre={grupo.label} />
          <BotonVerDocumento docId={doc.doc_id} filename={doc.filename} compacto="boton" />
          {doc.drive_url && (
            <a href={doc.drive_url} target="_blank" rel="noreferrer" className="btn-row"
               title="Abrir el archivo en Google Drive">
              Abrir en Drive
            </a>
          )}
          <button type="button" onClick={() => setVerDetalle((v) => !v)} aria-expanded={verDetalle}
                  className="ml-auto text-[11px] font-bold text-ink-faint hover:text-ink">
            Detalle {verDetalle ? '▾' : '▸'}
          </button>
        </div>
      )}
      {doc && verDetalle && <div className="bg-surface-alt px-3 pb-3 pt-1">{detalle(doc)}</div>}
      {/* DEF-111: el DNI que llegó en dos fotos se cumple con las dos. */}
      {grupo.otraCara && (
        <div className="flex flex-wrap items-center gap-1.5 border-t border-surface-faint px-3.5 py-2.5"
             data-otra-cara={grupo.otraCara.doc_id}>
          <span className="min-w-0 flex-1 truncate text-meta text-ink-muted">
            Otra cara: {grupo.otraCara.filename || 'archivo'}
          </span>
          <BotonDescargar docId={grupo.otraCara.doc_id} nombre={`${grupo.label} (otra cara)`} />
          <BotonVerDocumento docId={grupo.otraCara.doc_id} filename={grupo.otraCara.filename}
                             compacto="boton" />
        </div>
      )}
      {grupo.archivos.length > 0 && (
        <div className="border-t border-surface-faint">
          <button type="button" onClick={() => setVerAnteriores((v) => !v)} aria-expanded={verAnteriores}
                  className="flex w-full items-center justify-between bg-surface-alt px-3.5 py-[9px] text-left text-[11px] font-bold text-ink-muted">
            <span>Intentos anteriores ({grupo.archivos.length})</span>
            <span aria-hidden>{verAnteriores ? '▾' : '▸'}</span>
          </button>
          {verAnteriores && grupo.archivos.map((fila, i) => (
            <FilaDeIntento key={fila.doc.doc_id} fila={fila} detalle={detalle}
                           ultimaSinBorde={i === grupo.archivos.length - 1} />
          ))}
        </div>
      )}
    </div>
  )
}

/** Lo que una persona rechazó: por qué, y si el cliente ya lo sabe. */
function GrupoRechazado({ grupo, detalle, onReview, readOnly }: {
  grupo: GrupoDelRequisito<ConversationDocument>
  detalle: (doc: ConversationDocument) => ReactNode
  onReview: AlRevisar
  readOnly: boolean
}) {
  const pedido = grupo.estado === 'pedido'
  const rechazado = grupo.rechazado?.doc_id || grupo.requisito.documento_id || ''
  return (
    <div className="overflow-hidden rounded-panel border border-danger-border bg-white" data-requisito={grupo.estado}>
      <div className="flex flex-col gap-2 px-3.5 py-3">
        <div className="flex items-start justify-between gap-2">
          <p className="text-body font-bold leading-[1.35] text-ink">{grupo.label}</p>
          <span className={`shrink-0 ${pedido ? 'badge-blue' : 'badge-red'}`}>
            {pedido ? '↻ Pedido de nuevo' : '✕ Rechazado'}
          </span>
        </div>
        <p className={`text-meta leading-[1.35] ${pedido ? 'text-info-ink' : 'text-danger-ink'}`}>{grupo.sub}</p>
        {!pedido && !readOnly && rechazado && (
          <AvisoDeRechazo docId={rechazado} onReview={onReview} clienteAvisado={grupo.clienteAvisado} />
        )}
      </div>
      {grupo.archivos.length > 0 && (
        <div className="border-t border-surface-faint">
          {grupo.archivos.map((fila, i) => (
            <FilaDeIntento key={fila.doc.doc_id} fila={fila} detalle={detalle}
                           ultimaSinBorde={i === grupo.archivos.length - 1} />
          ))}
        </div>
      )}
    </div>
  )
}

/** Lo que no llegó, con la hora en que se pidió. */
function ListaSinRecibir({ grupos }: { grupos: Array<GrupoDelRequisito<ConversationDocument>> }) {
  return (
    <div className="overflow-hidden rounded-panel border border-surface-border bg-white">
      {grupos.map((g, i) => (
        <div key={g.clave} className={`flex justify-between gap-2 px-3.5 py-2.5 text-body ${
          i > 0 ? 'border-t border-surface-faint' : ''}`}>
          <span className="text-ink-soft">○ {g.label}</span>
          {g.hora && <span className="shrink-0 text-meta text-ink-faint">pedido {g.hora}</span>}
        </div>
      ))}
    </div>
  )
}

/**
 * Lo que el agente concluyó de ESTE archivo, con lo que leyó el cliente y las
 * alertas guardadas bajo él. Es lo primero de la tarjeta debajo del nombre:
 * es lo que el ejecutivo viene a mirar (TEST, 19-oct-2026).
 */
function BloqueVeredicto({ lectura }: { lectura: LecturaDelVeredicto }) {
  return (
    <div className={`mx-3.5 mb-3 rounded-field border px-2.5 py-2 text-meta ${TONO_BLOQUE[lectura.tono]}`}
         data-veredicto={lectura.grupo}>
      <p className={`font-bold ${TONO_TITULO[lectura.tono]}`}>
        {lectura.titulo}
        {lectura.intento && <span className="font-normal"> · {lectura.intento}</span>}
      </p>
      {lectura.requisitos && <p className="mt-0.5 text-ink-muted">{lectura.requisitos}</p>}
      {lectura.motivo && <p className="mt-0.5 text-ink">{lectura.motivo}</p>}
      {lectura.observaciones.length > 0 && (
        <ul className="mt-1 flex flex-col gap-0.5">
          {lectura.observaciones.map((o) => (
            <li key={o} className="text-warn-ink">⚠ {o}</li>
          ))}
        </ul>
      )}
      {lectura.textoCliente && (
        <p className="mt-1 text-ink-muted">
          <span className="font-bold">Lo que leyó el cliente:</span> «{lectura.textoCliente}»
        </p>
      )}
      {lectura.nota && <p className="mt-1 italic text-ink-faint">{lectura.nota}</p>}
      {lectura.persona && <p className="mt-1 font-bold text-ink">{lectura.persona}</p>}
    </div>
  )
}

function DocumentoCard({ doc, version = '', marca = null, onReview, busy, readOnly = false,
                        decide = !readOnly }: {
  doc: ConversationDocument
  /** §9.15 (DEF-056): «Versión vigente · llegó 05/10 16:35», o ''. */
  version?: string
  /** Lo que decidió el asesor sobre ESTE archivo y sigue pendiente (DEF-051). */
  marca?: MarcaDeRevision | null
  onReview: AlRevisar
  busy: boolean
  /** Sin lo que le habla al cliente: «Pedir de nuevo». */
  readOnly?: boolean
  /** Validar y rechazar: también en En vivo, para supervisión. */
  decide?: boolean
}) {
  const enProceso = ['PENDING', 'PROCESSING', 'UPLOADED', 'PENDING_DRIVE_SYNC', 'EVALUATED']
    .includes(doc.status)
  const campos = Object.entries(doc.extracted_fields ?? {})
  // Desde el 20-oct-2026 el ejecutivo puede validar o rechazar CUALQUIER
  // archivo que nadie decidió todavía —también lo que la IA validó, para
  // rechazarlo—, no solo lo que el clasificador dejó por revisar. Lo que se
  // está leyendo, lo que ya decidió una persona y lo que no es un documento, no.
  const revisable = !enProceso && !doc.rechazado_en_ingesta
    && !['APPROVED', 'REJECTED', 'SEPARADO', 'PROCESSING_FAILED'].includes(doc.status)
  // DEF-044: rechazado y el cliente sin avisar. «Pedir de nuevo al cliente»
  // pasa al frente, en el lugar donde estaba «Rechazar».
  const avisarAlCliente = ofrecerPedirDeNuevo(marca)
  const filasRecibo = filasDelRecibo(doc.recibo)
  const cruce = cruceDelRecibo(doc.recibo)
  // Mientras se lee no hay veredicto que mostrar: lo dice el aviso de abajo.
  const lectura = enProceso ? null : lecturaDelVeredicto(doc)

  return (
    <div className="card-panel overflow-hidden" data-grupo={lectura?.grupo ?? ''}>
      <div className="flex gap-3 p-3.5">
        <div className="flex h-[66px] w-[52px] shrink-0 items-center justify-center rounded-field border border-surface-border bg-surface-subtle text-[9px] font-bold text-ink-faint">
          {(doc.doc_type || 'DOC').slice(0, 6)}
        </div>
        <div className="min-w-0 flex-1">
          <p className="truncate text-body font-bold text-ink">{doc.filename || doc.type}</p>
          <p className="font-mono text-meta text-ink-faint">
            {doc.type}
            {doc.created_at && ` · ${new Date(doc.created_at).toLocaleDateString('es-PE')}`}
          </p>
          <div className="mt-1.5 flex flex-wrap gap-1">
            {/* El veredicto va primero. «En Drive» es dónde quedó guardado y
                el número es la confianza del CLASIFICADOR en el tipo: ninguno
                de los dos dice si el documento sirve (TEST, 19-oct-2026). */}
            {lectura && (
              <span className={`inline-flex items-center rounded-chip px-2 py-0.5 text-pill font-bold ${TONO_CHIP[lectura.tono]}`}>
                {lectura.chip}
              </span>
            )}
            <StatusPill status={doc.status} kind="document" />
            {doc.confidence && (
              <span className="inline-flex items-center rounded-chip bg-purple-light px-2 py-0.5 text-pill font-bold text-purple"
                    title="Confianza del clasificador en el tipo de documento: no es la validación">
                Tipo {doc.confidence}
              </span>
            )}
            {/* §9.15. Solo cuando HUBO duplicados: con una sola versión el
                campo viene vacío y no se pinta nada. Las dos caras se muestran
                —la que se usa y la que no— porque el analista necesita saber
                que la otra existe y dónde mirar si la elegida salió mal.
                DEF-056: con la hora de llegada, no «1 de 2» (`documentos.ts`). */}
            {version && (
              <span className={`inline-flex items-center rounded-chip px-2 py-0.5 text-pill font-bold ${
                doc.version_preferida === 'si'
                  ? 'bg-brand-light text-brand-hover'
                  : 'bg-surface-subtle text-ink-faint'}`}>
                {version}
              </span>
            )}
            {/* Casos 3.1–3.5: es de otro inmueble y se apartó al elegir. */}
            {doc.otro_inmueble && (
              <span className="inline-flex items-center rounded-chip bg-surface-subtle px-2 py-0.5 text-pill font-bold text-ink-faint"
                    title="El cliente eligió otro inmueble: este documento quedó apartado, no se borró.">
                Otro inmueble{doc.otro_inmueble !== 'sí' ? ` · Partida ${doc.otro_inmueble}` : ''}
              </span>
            )}
            {/* DEF-051: la misma marca que la Ficha, sobre el archivo. */}
            {marca && (
              <span className="inline-flex items-center rounded-chip border border-warn-border bg-warn-light px-2 py-0.5 text-pill font-bold text-warn">
                {marca.corto}
              </span>
            )}
          </div>
        </div>
      </div>

      {lectura && <BloqueVeredicto lectura={lectura} />}

      {marca && (
        <p className="mx-3.5 mb-3 rounded-field border border-warn-border bg-warn-light px-2.5 py-1.5 text-meta text-warn-ink">
          <span className="font-bold">{marca.titulo}</span>
          {marca.detalle && ` · ${marca.detalle}`}
        </p>
      )}

      {enProceso && (
        <p className="mx-3.5 mb-3 rounded-field bg-info-light px-2.5 py-1.5 text-meta text-info">
          Estamos leyendo el documento. Suele tardar menos de un minuto.
        </p>
      )}

      {doc.summary && (
        <p className="mx-3.5 mb-2 text-meta text-ink-muted">{doc.summary}</p>
      )}

      {doc.risks.length > 0 && (
        <ul className="mx-3.5 mb-2 flex flex-col gap-1">
          {doc.risks.map((r, i) => (
            <li key={i} className="rounded-field bg-warn-light px-2.5 py-1.5 text-meta text-warn-ink">
              {r}
            </li>
          ))}
        </ul>
      )}

      {campos.length > 0 && (
        <div className="flex flex-col gap-1 px-3.5 pb-3">
          {campos.map(([clave, valor]) => {
            // La discrepancia se marca en ámbar: es el caso del documento cuyo
            // titular no coincide con el del contacto, y es lo que el asesor
            // tiene que mirar antes de aprobar.
            const discrepa = Boolean(doc.mismatch_reason) &&
              doc.mismatch_reason.toLowerCase().includes(clave.toLowerCase())
            return (
              <div key={clave} className={discrepa ? 'kv-mismatch' : 'kv'}>
                <span className={discrepa ? 'font-semibold' : 'text-ink-muted'}>{clave}</span>
                <span className="font-mono">{String(valor) || '—'}</span>
              </div>
            )
          })}
        </div>
      )}

      {/* DEF-054: lo propio del recibo. El titular es informativo —ninguna
          regla lo compara—; el cruce es el que decidió el sistema contra la
          Copia Literal (`recibo.cruce_cl`), dicho en palabras (`recibo.ts`). */}
      {filasRecibo.length > 0 && (
        <div className="flex flex-col gap-1 px-3.5 pb-3">
          {filasRecibo.map((f) => (
            <div key={f.clave} className="kv">
              <span className="text-ink-muted">{f.clave}</span>
              <span className={f.falta ? 'italic text-ink-faint' : 'font-mono'}>{f.valor}</span>
            </div>
          ))}
          {cruce && (
            <p className={`mt-1 rounded-field px-2.5 py-1.5 text-meta font-bold ${
              cruce.tono === 'ok'
                ? 'bg-brand-light text-brand-hover'
                : cruce.tono === 'mal'
                ? 'border border-danger-border bg-danger-light text-danger-ink'
                : 'bg-surface-subtle text-ink-muted'}`}>
              {cruce.texto}
            </p>
          )}
        </div>
      )}

      {doc.mismatch_reason && (
        <p className="mx-3.5 mb-3 rounded-field border border-warn-border bg-warn-light px-2.5 py-2 text-meta text-warn-ink">
          {doc.mismatch_reason}
        </p>
      )}

      {/* Abrir el archivo. Son dos caminos distintos a propósito: la vista
          previa es una URL firmada de S3 que vence en minutos y sirve para
          mirarlo acá mismo; Drive es donde queda archivado. */}
      <div className="flex flex-wrap gap-1.5 border-t border-surface-faint px-3.5 py-2.5">
        <BotonVerDocumento docId={doc.doc_id} filename={doc.filename} />
        {doc.drive_url && (
          <a
            href={doc.drive_url}
            target="_blank"
            rel="noreferrer"
            className="btn-row"
            title="Abrir el archivo en Google Drive"
          >
            Abrir en Drive
          </a>
        )}
      </div>

      {/* La DECISIÓN sobre el documento: aprobar o rechazar cambian su
          estado y el del requisito que cubre. */}
      {/* Validar y rechazar son decisiones definitivas del ejecutivo, con
          confirmación (diseño «Turno 2», 20-oct-2026). Supervisión también
          decide desde En vivo, sin tomar la conversación. */}
      {revisable && decide && (
        <div className="flex flex-col gap-1.5 border-t border-surface-faint bg-surface-alt px-3.5 py-2.5">
          <span className="text-meta font-bold text-ink-muted">Revisión</span>
          <DecisionDelEjecutivo
            archivo={doc}
            observacionIA={lectura && lectura.grupo === 'observados' ? lectura.motivo || lectura.titulo : ''}
            sugerido={motivoSugerido(doc.veredicto_agente?.case_id)}
            busy={busy}
            conVer={false}
            onValidar={() => onReview(doc.doc_id, 'approve')}
            onRechazar={(motivo) => onReview(doc.doc_id, 'reject', motivo)}
          />
        </div>
      )}

      {/* DEF-043: «Pedir de nuevo» es un MENSAJE al cliente, no una decisión
          sobre el documento, y va aparte. No depende de que el documento esté
          por revisar: lo natural después de rechazar es pedirlo de nuevo, y
          un rechazado ya no es revisable.
          DEF-044: después de «Rechazar» esta fila queda donde estaba la de la
          revisión y pasa al frente —«Pedir de nuevo al cliente», en ámbar—
          mientras el cliente no sepa del rechazo. Abre el mismo pedido, con
          su chip en el compositor. */}
      {!enProceso && !readOnly && (
        <div className={`flex flex-wrap items-center gap-1.5 border-t px-3.5 py-2.5 ${
          avisarAlCliente ? 'border-warn-border bg-warn-light' : 'border-surface-faint'}`}>
          <span className={`mr-auto text-meta ${avisarAlCliente ? 'font-bold text-warn-ink' : 'text-ink-muted'}`}>
            {avisarAlCliente
              ? 'El cliente todavía no sabe que lo rechazaste: pídele que lo vuelva a enviar.'
              : 'Mensaje al cliente: el requisito queda pendiente y el agente recibe el reenvío.'}
          </span>
          <button
            disabled={busy}
            onClick={() => onReview(doc.doc_id, 'request')}
            className={`${avisarAlCliente ? 'btn-soft' : 'btn-row'} disabled:opacity-50`}
          >
            {avisarAlCliente ? '✉ Pedir de nuevo al cliente' : '✉ Pedir de nuevo'}
          </button>
        </div>
      )}
    </div>
  )
}

/**
 * Abre el documento en el visor del portal, encima de la bandeja.
 *
 * Hasta el 28-set-2026 abría una pestaña nueva, y el asesor perdía el chat de
 * vista para mirar un DNI. La URL firmada la pide el visor al abrirse —vence a
 * los 5 minutos—, no se genera al cargar la lista.
 */
function BotonVerDocumento({ docId, filename, compacto = false }: {
  docId: string
  filename: string
  /** «Ver» corto: `texto` en la Ficha, `borde` en la fila de un intento,
   *  `boton` al lado de «↓ Descargar». Sin él, «Ver documento». */
  compacto?: false | 'texto' | 'borde' | 'boton'
}) {
  const [abierto, setAbierto] = useState(false)
  const clase = compacto === 'texto'
    ? 'shrink-0 text-[11px] font-bold text-brand-hover hover:underline'
    : compacto === 'borde'
    ? 'shrink-0 rounded-field border border-surface-border px-[9px] py-1 text-[11px] font-bold text-ink-muted hover:border-brand hover:text-brand-hover'
    : 'btn-row'
  return (
    <>
      <button type="button" onClick={() => setAbierto(true)} className={clase}>
        {compacto ? 'Ver' : 'Ver documento'}
      </button>
      {abierto && (
        <VisorDeDocumento documentId={docId} filename={filename}
                          onClose={() => setAbierto(false)} />
      )}
    </>
  )
}

// ── Ficha ────────────────────────────────────────────────────────────────────

/** Ícono y colores de cada estado del requisito. */
const ASPECTO_DEL_REQUISITO: Record<EstadoDelRequisito, { icono: string; icon: string; sub: string }> = {
  sin_recibir: { icono: '○', icon: 'text-ink-ghost', sub: 'text-ink-faint' },
  observado: { icono: '⚠', icon: 'text-warn-dot', sub: 'text-warn-ink' },
  validado: { icono: '✓', icon: 'text-brand', sub: 'text-brand-hover' },
  rechazado: { icono: '✕', icon: 'text-danger', sub: 'text-danger-ink' },
  pedido: { icono: '↻', icon: 'text-info', sub: 'text-info-ink' },
  no_aplica: { icono: '–', icon: 'text-ink-ghost', sub: 'text-ink-faint' },
}

function plural(n: number, uno: string, varios: string): string {
  return `${n} ${n === 1 ? uno : varios}`
}

/**
 * Un archivo que PODRÍA ser del requisito (mismo tipo, sin veredicto
 * registrado: llegó antes del 20-oct-2026, como los del caso de Flor). Si
 * nadie decidió, el ejecutivo lo valida o lo rechaza; si ya se decidió, se dice
 * quién y qué.
 */
function CandidatoDelRequisito({ doc, grupo, decision }: {
  doc: ConversationDocument
  grupo: GrupoDelRequisito<ConversationDocument>
  decision: Decision
}) {
  const decidido = doc.status === 'APPROVED' || doc.status === 'REJECTED'
  const lectura = lecturaDelVeredicto(doc)
  const observacion = (grupo.requisito.observacion || '').trim()
  return (
    <div className="flex flex-col gap-1.5 rounded-field bg-surface px-[9px] py-1.5">
      <div className="flex items-center gap-2">
        <p className="min-w-0 flex-1 truncate font-mono text-meta text-ink" title={doc.filename}>
          {doc.filename || doc.type}
        </p>
        {(decidido || !decision) && (
          <BotonVerDocumento docId={doc.doc_id} filename={doc.filename} compacto="texto" />
        )}
      </div>
      {decidido && <p className="text-meta font-bold text-ink-muted">{lectura.persona}</p>}
      {!decidido && decision && (
        <DecisionDelEjecutivo
          archivo={doc} busy={decision.reviewing === doc.doc_id}
          observacionIA={`El requisito quedó observado por la IA${observacion ? ` (${observacion})` : ''}; no quedó registrado si fue por este archivo.`}
          sugerido={grupo.motivoSugerido}
          onValidar={() => decision.onReview(doc.doc_id, 'approve')}
          onRechazar={(motivo) => decision.onReview(doc.doc_id, 'reject', motivo)} />
      )}
    </div>
  )
}

/**
 * Una fila del checklist: qué archivo lo cumple —validado por la IA o por una
 * persona, y se baja de acá— o por qué no. Lo que la IA observó lleva la
 * decisión del ejecutivo: validarlo igual o rechazarlo, definitivas y con
 * confirmación (Diego Casallo, 20-oct-2026).
 *
 * Plegada por defecto (Diego, 20-oct-2026: «que se pueda expandir o disminuir
 * para que así no se llene de archivos»): la cabecera dice el estado y lo que
 * espera adentro —«3 archivos por decidir»— y el detalle se abre al tocarla
 * (`plegadoDeLaFila`). Lo validado se baja sin abrirla.
 */
function FilaDeRequisito({ grupo, conversationId, decision, avisaAlCliente }: {
  grupo: GrupoDelRequisito<ConversationDocument>
  conversationId: string
  decision: Decision
  /** «Pedir de nuevo» tras rechazar: es un mensaje, y En vivo no escribe. */
  avisaAlCliente: boolean
}) {
  const r = grupo.requisito
  const [verIntentos, setVerIntentos] = useState(false)
  const [abierta, setAbierta] = useState(false)
  useEffect(() => { setVerIntentos(false); setAbierta(false) }, [conversationId])
  // DEF-022: los intentos que se pusieron en cero al retomar con el agente,
  // solo si los hubo.
  const previos = intentosAnteriores(r.intentos_previos)
  const a = ASPECTO_DEL_REQUISITO[grupo.estado]
  const doc = grupo.aDecidir
  const observado = grupo.estado === 'observado'
  const plegado = plegadoDeLaFila(grupo)
  const conDetalle = !plegado.plegable || abierta
  const cabecera = (
    <>
      <span className={`w-[18px] shrink-0 text-[13px] font-extrabold ${a.icon}`}>{a.icono}</span>
      <span className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className={`text-body font-semibold leading-[1.35] ${
          grupo.estado === 'no_aplica' ? 'text-ink-faint line-through' : 'text-ink'}`}>
          {r.label}
        </span>
        <span className={`break-words text-meta leading-[1.35] ${a.sub}`}>
          {previos ? `${grupo.sub} · ${previos}` : grupo.sub}
        </span>
        {plegado.plegable && (
          <span className={`text-[11px] font-bold ${observado ? 'text-warn' : 'text-brand-hover'}`}>
            {abierta ? '▾ Ocultar el detalle' : `▸ ${plegado.resumen}`}
          </span>
        )}
      </span>
    </>
  )
  return (
    <div className="border-t border-surface-faint" data-requisito={grupo.estado}
         data-plegada={plegado.plegable ? String(!abierta) : undefined}>
      <div className="flex items-start gap-2.5 px-4 py-2.5">
        {plegado.plegable ? (
          <button type="button" onClick={() => setAbierta((v) => !v)} aria-expanded={abierta}
                  className="-m-1 flex min-w-0 flex-1 items-start gap-2.5 rounded-field p-1 text-left transition-colors hover:bg-surface-alt">
            {cabecera}
          </button>
        ) : (
          <div className="flex min-w-0 flex-1 items-start gap-2.5">{cabecera}</div>
        )}
        {grupo.estado === 'validado' && grupo.archivo && (
          <BotonDescargar docId={grupo.archivo.doc_id} nombre={r.label} compacto />
        )}
        {grupo.estado === 'validado' && grupo.otraCara && (
          <BotonDescargar docId={grupo.otraCara.doc_id} nombre={`${r.label} (otra cara)`} compacto />
        )}
      </div>

      {conDetalle && (
        <>
          {grupo.estado === 'validado' && grupo.observaciones.length > 0 && (
            <ul className="mb-2 ml-11 mr-4 flex flex-col gap-0.5">
              {grupo.observaciones.map((o) => <li key={o} className="text-meta text-warn-ink">⚠ {o}</li>)}
            </ul>
          )}
          {observado && grupo.observacionIA && (
            <div className="mb-2 ml-11 mr-4 rounded-field bg-warn-light px-2.5 py-[7px]">
              <p className="text-[10px] font-bold uppercase tracking-[0.05em] text-warn">Observación del agente IA</p>
              <p className="mt-0.5 text-[11px] leading-[1.4] text-warn-ink">{grupo.observacionIA}</p>
            </div>
          )}
          {observado && grupo.archivos.length > 1 && (
            <button type="button" onClick={() => setVerIntentos((v) => !v)} aria-expanded={verIntentos}
                    className="mb-2 ml-11 text-[11px] font-bold text-brand-hover">
              {verIntentos ? '▾' : '▸'} Ver los intentos anteriores
            </button>
          )}
          {observado && verIntentos && (
            <div className="mb-2 ml-11 mr-4 flex flex-col gap-1">
              {grupo.archivos.map((f) => (
                <div key={f.doc.doc_id} className="flex items-center gap-2 rounded-field bg-surface px-[9px] py-[5px]">
                  <p className="min-w-0 flex-1 truncate text-meta text-ink-soft" title={f.doc.filename}>
                    <span className="font-mono">{f.n} · {f.doc.filename}</span> — {f.motivo}
                  </p>
                  <BotonVerDocumento docId={f.doc.doc_id} filename={f.doc.filename} compacto="texto" />
                </div>
              ))}
            </div>
          )}
          {grupo.candidatos.length > 0 && (
            <div className="mb-3 ml-11 mr-4 flex flex-col gap-1.5" data-candidatos={grupo.candidatos.length}>
              <p className="text-meta leading-[1.35] text-ink-muted">
                Estos archivos llegaron antes de que se registrara qué observó la IA en cada uno.
                {decision ? ' Elige cuál validar o rechazar.' : ''}
              </p>
              {grupo.candidatos.map((c) => (
                <CandidatoDelRequisito key={c.doc_id} doc={c} grupo={grupo} decision={decision} />
              ))}
            </div>
          )}

          {doc && decision && (
            <div className="pb-3 pl-11 pr-4">
              <DecisionDelEjecutivo archivo={doc} busy={decision.reviewing === doc.doc_id}
                                    observacionIA={grupo.observacionIA || grupo.sub}
                                    sugerido={grupo.motivoSugerido}
                                    onValidar={() => decision.onReview(doc.doc_id, 'approve')}
                                    onRechazar={(motivo) => decision.onReview(doc.doc_id, 'reject', motivo)} />
            </div>
          )}
        </>
      )}
      {/* El aviso del rechazo no se pliega: es una acción pendiente, no archivos. */}
      {grupo.estado === 'rechazado' && decision && avisaAlCliente
        && (grupo.rechazado?.doc_id || r.documento_id) && (
        <div className="pb-3 pl-11 pr-4">
          <AvisoDeRechazo docId={grupo.rechazado?.doc_id || r.documento_id || ''}
                          onReview={decision.onReview} clienteAvisado={grupo.clienteAvisado} />
        </div>
      )}
    </div>
  )
}

function TabFicha({ detail, onReview, reviewing, readOnly = false, decide = !readOnly }: {
  detail: ConversationDetail
  onReview: AlRevisar
  reviewing: string | null
  readOnly?: boolean
  /** Validar y rechazar, también en solo lectura (`puedeDecidir`). */
  decide?: boolean
}) {
  const respuestas = Object.entries(detail.tree_answers ?? {}).filter(([, v]) => v)
  const progreso = detail.progreso

  // Solicitante y DNI del caso (DEF-024), teléfono real o por qué no lo hay y
  // usuario de WhatsApp (DEF-018), cuestionario y lo detectado en documentos
  // —sin el «Nombre detectado», que es de cada archivo—: `ficha.ts`.
  const datos = filasDeLaFicha(detail)
  const hayDeDocumentos = datos.some((f) => f.origen === 'documento')

  // DEF-040: lo que no aplica (el DNI del titular fallecido) no cuenta en el
  // denominador.
  const aplicables = progreso
    ? progreso.requeridos.filter((r) => !r.no_aplica).length
    : 0
  // Cada requisito con su estado, lo que dijo la IA y la decisión del
  // ejecutivo sobre lo observado (20-oct-2026); lo validado se baja de acá. Lo
  // pedido de nuevo dice «esperando reenvío» en su fila (DEF-051).
  const caso = agruparPorRequisito(separarRechazados(detail.documents).tarjetas,
                                   progreso?.requeridos, detail.checklist_generated_at ?? '')
  const decision: Decision = decide ? { onReview, reviewing } : null
  const ancho = (n: number) => `${aplicables ? (n / aplicables) * 100 : 0}%`

  return (
    <div className="flex flex-col gap-3">
      {progreso && progreso.requeridos.length > 0 && (
        <div className="card-panel overflow-hidden">
          <div className="px-4 pb-3 pt-4">
            <div className="flex items-baseline justify-between">
              <p className="text-card font-extrabold text-ink">Documentos del caso</p>
              <span className="font-mono text-micro text-ink-muted">
                {caso.validados}/{aplicables} validados
              </span>
            </div>
            <div className="mt-2 flex h-1.5 overflow-hidden rounded-full bg-surface-subtle">
              <div className="h-full bg-brand transition-all" style={{ width: ancho(caso.validados) }} />
              <div className="h-full bg-warn-dot transition-all" style={{ width: ancho(caso.observados) }} />
            </div>
            <div className="mt-1.5 flex flex-wrap gap-x-3 gap-y-1 text-meta text-ink-muted">
              <span><span className="text-brand">●</span> {plural(caso.validados, 'validado', 'validados')}</span>
              <span><span className="text-warn-dot">●</span> {plural(caso.observados, 'observado', 'observados')}</span>
              <span><span className="text-danger">●</span> {plural(caso.rechazados, 'rechazado', 'rechazados')}</span>
              <span><span className="text-surface-strong">●</span> {caso.sinRecibir} sin recibir</span>
            </div>
            {readOnly && caso.observados > 0 && (
              <p className="mt-2 rounded-field bg-info-light px-2.5 py-1.5 text-meta text-info">
                {decide
                  ? 'Lo que valides o rechaces queda a tu nombre. La conversación sigue con '
                    + 'quien la tiene asignada y al cliente no se le escribe nada.'
                  : 'Solo lectura: lo observado lo valida o lo rechaza quien tiene asignada la '
                    + 'conversación, desde Mi bandeja.'}
              </p>
            )}
          </div>
          {caso.grupos.map((g) => (
            <FilaDeRequisito key={g.clave} grupo={g} conversationId={detail.conversation_id}
                             decision={decision} avisaAlCliente={!readOnly} />
          ))}
          <div className="flex flex-col gap-1.5 border-t border-surface-faint px-4 py-3">
            <BotonDescargarValidados archivos={paraDescargar(caso.descargables)} variante="principal" />
            <p className="text-center text-meta text-ink-faint">
              Lo validado por la IA o por un ejecutivo para cada requisito. Se descarga uno por uno,
              con el nombre del requisito.
            </p>
          </div>
        </div>
      )}

      <div className="card-panel p-4">
        <p className="text-card font-extrabold text-ink">Datos del cliente</p>
        {datos.length === 0 ? (
          <p className="mt-1.5 text-micro text-ink-muted">
            Todavía no se capturó ningún dato en esta conversación.
          </p>
        ) : (
          <div className="mt-2.5 flex flex-col">
            {datos.map(({ clave, valor, detalle }) => (
              <div
                key={clave}
                className="flex items-center justify-between gap-3 border-t border-surface-subtle py-2 text-body first:border-t-0"
              >
                <span className="text-ink-muted">{clave}</span>
                <span className="text-right">
                  <span className="block font-bold text-ink">{valor}</span>
                  {detalle && <span className="block text-meta text-ink-faint">{detalle}</span>}
                </span>
              </div>
            ))}
          </div>
        )}
        <p className="mt-2.5 text-meta text-ink-faint">
          {hayDeDocumentos
            ? 'Solicitante, DNI y teléfono son los del caso. Lo demás que no viene del cuestionario '
              + 'lo detectó la IA en los documentos: confirmalo con el cliente antes de darlo por bueno.'
            : 'Solicitante, DNI y teléfono son los del caso; el resto se completa con el cuestionario '
              + 'del bot y con lo que la IA detecte en los documentos.'}
        </p>
      </div>

      {respuestas.length > 0 && (
        <div className="card-panel p-4">
          <p className="text-card font-extrabold text-ink">Respuestas del cuestionario</p>
          <ol className="mt-2.5 flex list-decimal flex-col gap-1.5 pl-4 text-body text-ink">
            {respuestas.map(([nodo, respuesta]) => (
              <li key={nodo}>
                <span className="text-ink-muted">{nodo}: </span>
                {String(respuesta)}
              </li>
            ))}
          </ol>
        </div>
      )}

      {detail.alerts.length > 0 && (
        <div className="card-panel p-4">
          <p className="text-card font-extrabold text-ink">Alertas internas</p>
          <div className="mt-2.5 flex flex-col gap-2">
            {detail.alerts.map((a, i) => (
              <div key={i} className={a.critica
                ? 'rounded-field border border-danger-border bg-danger-light px-2.5 py-2'
                : 'rounded-field bg-surface px-2.5 py-2'}>
                <p className={`text-meta font-bold uppercase tracking-[0.05em] ${
                  a.critica ? 'text-danger' : 'text-ink-faint'}`}>
                  {a.team}{a.critica && ' · Alerta crítica'}
                </p>
                <p className={`mt-0.5 text-body ${a.critica ? 'font-semibold text-danger-ink' : 'text-ink'}`}>
                  {a.text}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
